import * as React from "react"
import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Checkbox } from "src/components/ui/checkbox"
import i18n from "src/i18n"

const STORAGE_KEY_PREFIX = "ecosuite_login_popup_dismissed"
const { t } = i18n

interface LoginPopupProps {
  nextAccess: boolean
  userId?: string
}

export function LoginPopup({ nextAccess, userId }: LoginPopupProps) {
  const [open, setOpen] = useState(false)
  const [rememberChoice, setRememberChoice] = useState(false)

  const storageKey = userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX

  useEffect(() => {
    if (nextAccess && userId && localStorage.getItem(storageKey) !== "true") {
      setOpen(true)
    }
  }, [nextAccess, userId, storageKey])

  const handleDismiss = () => {
    if (rememberChoice) {
      localStorage.setItem(storageKey, "true")
    }
    setOpen(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleDismiss()
    }
  }

  const v2Url = process.env.REACT_APP_V2_URL ?? "https://app2.ecosuitetest.io"

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="login-info-popup tw-max-w-lg">
        <DialogHeader>
          <DialogTitle className="login-info-popup-title">{t("login_popup.title")}</DialogTitle>
          <DialogDescription asChild>
            <div className="tw-space-y-4 tw-text-sm tw-text-muted-foreground">
              <p>{t("login_popup.greeting")}</p>
              <p>{t("login_popup.info")}</p>
              <p>
                {t("login_popup.redirect")}{" "}
                <a
                  href={v2Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-primary tw-underline tw-font-medium"
                >
                  {t("login_popup.redirect_link")}
                </a>
              </p>
              <p>{t("login_popup.continue")}</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Checkbox
              id="remember-choice"
              checked={rememberChoice}
              onCheckedChange={(checked) => setRememberChoice(checked === true)}
            />
            <label htmlFor="remember-choice" className="tw-text-sm tw-cursor-pointer">
              {t("login_popup.remember_choice")}
            </label>
          </div>
          <Button onClick={handleDismiss}>{t("login_popup.dismiss_button")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
